'use server'

/**
 * Server actions for the notification bell and page (§102).
 *
 * Like every other write in this app these forward the caller's session cookie
 * and the API re-checks it; the browser never holds a credential and never talks
 * to the backend directly. The bell is a client component, so these are its only
 * way to reach the API — which is the point: the ownership rule lives in the
 * service, one hop past anything a browser can call.
 *
 * Every one returns a plain value rather than throwing. These are called from
 * click handlers, and a rejected promise there is an unhandled error overlay
 * where a quiet no-op would do.
 */
import { revalidatePath } from 'next/cache'
import { apiGet, apiSend } from '@/lib/session'
import type { NotificationPage } from '@/lib/notifications'

const EMPTY: NotificationPage = { notifications: [], unread: 0, total: 0 }

/**
 * The dropdown's payload.
 *
 * Returns an empty page on any failure, and that is deliberate: the bell hangs
 * off the account chip on every signed-in page in the product, and a header that
 * throws because a notification count timed out would take the whole page with
 * it. An empty bell is a small wrongness; a 500 on the dashboard is not.
 */
export async function fetchNotifications(limit = 12): Promise<NotificationPage> {
  return (await apiGet<NotificationPage>(`/api/notifications?limit=${limit}`)) ?? EMPTY
}

/** Just the badge. Its own call because it is the one that gets polled. */
export async function fetchUnreadCount(): Promise<number> {
  const res = await apiGet<{ unread: number }>('/api/notifications/count')
  return res?.unread ?? 0
}

/** `ids` omitted marks everything read — the "Mark all read" button. */
export async function markNotificationsRead(ids?: string[]): Promise<number> {
  const res = await apiSend<{ marked: number }>('/api/notifications/read', 'POST', { ids })
  revalidatePath('/dashboard/notifications')
  return res.data?.marked ?? 0
}

export async function markNotificationUnread(id: string): Promise<boolean> {
  const res = await apiSend(`/api/notifications/${id}/unread`, 'POST')
  revalidatePath('/dashboard/notifications')
  return res.ok
}

export async function removeNotification(id: string): Promise<boolean> {
  const res = await apiSend(`/api/notifications/${id}`, 'DELETE')
  revalidatePath('/dashboard/notifications')
  return res.ok
}

/**
 * Clear.
 *
 * `readOnly` is the dropdown's version — tidy away what has already been seen —
 * and the page's Clear passes `false` behind a confirmation, because a control
 * that can throw away something you have not read yet has to say so first.
 */
export async function clearNotifications(readOnly = true): Promise<number> {
  const res = await apiSend<{ deleted: number }>(
    `/api/notifications?read=${readOnly ? 'true' : 'false'}`,
    'DELETE',
  )
  revalidatePath('/dashboard/notifications')
  return res.data?.deleted ?? 0
}
